import { api } from "@/lib/api";
import { MetricsResult } from "@/types";
import { ActionIcon, Box, Group, LoadingOverlay, Modal, Paper, SimpleGrid, Stack, Table, Tabs, Text, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCategory, IconHome, IconMinus, IconPlus, IconPrinter, IconTable, IconUserCircle, IconUserExclamation, IconUserPlus, IconUsers, IconUsersGroup, IconUsersPlus } from "@tabler/icons-react";
import { useEffect, useState } from "react";

const Metrics = () => {
    const [metrics, setMetrics] = useState<MetricsResult | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [ apnEventsModalOpened, apnEventsModalCallbacks ] = useDisclosure(false);
    const [ nameEventsModalOpened, nameEventsModalCallbacks ] = useDisclosure(false);
    const [ apnModalOpened, apnModalCallbacks ] = useDisclosure(false);
    const [ nameModalOpened, nameModalCallbacks ] = useDisclosure(false);
    const [ countyUsersModalOpened, countyUsersModalCallbacks ] = useDisclosure(false);
    const [ activeUsers30DaysModalOpened, activeUsers30DaysModalCallbacks ] = useDisclosure(false);
    const [ newUsers7DaysModalOpened, newUsers7DaysModalCallbacks ] = useDisclosure(false);
    const [ newUsers30DaysModalOpened, newUsers30DaysModalCallbacks ] = useDisclosure(false);

    useEffect(() => {
        const getMetrics = async () => {
            try {
                const metricsResult: MetricsResult = await api.get(`/admin/metrics`);
                setMetrics(metricsResult);
            }
            catch (error) {
                console.error("Error fetching metrics:", error);
            }
            finally {
                setLoading(false);
            }
        }
        getMetrics();
    }, [])

    const handlePrint = () => {
        const iframe = document.getElementById('print-metrics-iframe') as HTMLIFrameElement;
        if (iframe) {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        }
    }

    return (
        <>
            <LoadingOverlay visible={loading} overlayProps={{ blur: 2, radius: 'sm' }} loaderProps={{ size: 'xl' }}></LoadingOverlay>
            {!metrics && (
                <Stack justify="center" align="center" style={{height: '100vh'}}>
                    <Text size="xl" c="dimmed">Loading metrics...</Text>
                </Stack>
            )}
            {metrics && 
            <Tabs defaultValue="table" orientation="vertical">
                <Tabs.List>
                    <Tabs.Tab value="table" leftSection={<IconTable size={14} />}>Table</Tabs.Tab>
                    <Tabs.Tab value="groups" leftSection={<IconCategory size={14} />}>Groups</Tabs.Tab>
                </Tabs.List>
                <Tabs.Panel m='md' value="table">
                     <Paper withBorder p='md' radius={'md'}>
                        <Stack align={'end'} justify={'flex-end'}>
                            <Tooltip label={'Print Report'} withArrow>
                                <ActionIcon aria-label="Print report" variant='outline' color='pq-red' onClick={handlePrint}>
                                    <IconPrinter size={16} />
                                </ActionIcon>
                            </Tooltip>
                            <Table striped highlightOnHover>
                                <Table.Thead>
                                    <Table.Tr>
                                        <Table.Th>County</Table.Th>
                                        <Table.Th>Total Users</Table.Th>
                                        <Table.Th>New Users Last 7 Days</Table.Th>
                                        <Table.Th>New Users Last 30 Days</Table.Th>
                                        <Table.Th>Active Users Last 30 Days</Table.Th>
                                        <Table.Th>Guarded Properties</Table.Th>
                                        <Table.Th>Guarded Names</Table.Th>
                                        <Table.Th>Property Found Events</Table.Th>
                                        <Table.Th>Name Found Events</Table.Th>
                                    </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                    <Table.Tr>
                                        <Table.Td>Total</Table.Td>
                                        <Table.Td>{metrics?.totalUsers ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalRegisteredInLast7Days ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalRegisteredInLast30Days ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.activeUsersInLast30Days ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalApnSubscriptions ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalNameSubscriptions ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalApnEvents ?? 0}</Table.Td>
                                        <Table.Td>{metrics?.totalNameEvents ?? 0}</Table.Td>
                                    </Table.Tr>
                                    {metrics.counties.map((county: string) => (
                                        <Table.Tr key={county}>
                                            <Table.Td>{county}</Table.Td>
                                            <Table.Td>{metrics?.usersByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.totalRegisteredInLast7DaysByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.totalRegisteredInLast30DaysByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.activeUsersInLast30DaysByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.subscriptionsApnByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.subscriptionsNameByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.apnEventsByCounty[county] ?? 0}</Table.Td>
                                            <Table.Td>{metrics?.nameEventsByCounty[county] ?? 0}</Table.Td>
                                        </Table.Tr>
                                    ))}
                                </Table.Tbody>
                            </Table>
                            <iframe src="/print-metrics" style={{display: 'none'}} id="print-metrics-iframe"></iframe>
                        </Stack>
                     </Paper>
                </Tabs.Panel>
                <Tabs.Panel m='md' value="groups">
                    <Stack>
                        <SimpleGrid cols={3}>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Total Users</Text>
                                    <IconUsersGroup size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalUsers ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle total users modal" variant="filled" onClick={countyUsersModalCallbacks.toggle} size={30}>
                                        {countyUsersModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Active Users Last 30 Days</Text>
                                    <IconUserCircle size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.activeUsersInLast30Days ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle active users last 30 days modal" variant="filled" onClick={activeUsers30DaysModalCallbacks.toggle} size={30}>
                                        {activeUsers30DaysModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>New Users Last 7 Days</Text>
                                    <IconUserPlus size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalRegisteredInLast7Days ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle new users last 7 days modal" variant="filled" onClick={newUsers7DaysModalCallbacks.toggle} size={30}>
                                        {newUsers7DaysModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>New Users Last 30 Days</Text>
                                    <IconUsersPlus size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalRegisteredInLast30Days ?? 0}</Text>
                                     <ActionIcon aria-label="Toggle new users last 30 days modal" variant="filled" onClick={newUsers30DaysModalCallbacks.toggle} size={30}>
                                        {newUsers30DaysModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Guarded Properties</Text>
                                    <IconHome size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalApnSubscriptions ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle guarded properties modal" variant="filled" onClick={apnModalCallbacks.toggle} size={30}>
                                        {apnModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Guarded Names</Text>
                                    <IconUsers size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalNameSubscriptions ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle guarded names modal" variant="filled" onClick={nameModalCallbacks.toggle} size={30}>
                                        {nameModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Property Found Events</Text>
                                    <IconUserExclamation size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalApnEvents ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle Property found events modal" variant="filled" onClick={apnEventsModalCallbacks.toggle} size={30}>
                                        {apnEventsModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                            <Paper withBorder p='md' radius={'md'}>
                                <Group justify='space-between'>
                                    <Text size='xs' c='dimmed'>Name Found Events</Text>
                                    <IconUserExclamation size={22} stroke={1.5} />
                                </Group>
                                <Group justify="space-between" align="flex-end" gap="xs" mt={25}>
                                    <Text>{metrics?.totalNameEvents ?? 0}</Text>
                                    <ActionIcon aria-label="Toggle name found events modal" variant="filled" onClick={nameEventsModalCallbacks.toggle} size={30}>
                                        {nameEventsModalOpened ? <IconMinus stroke={1.5} style={{width: '70%', height: '70%'}} /> : <IconPlus stroke={1.5} style={{width: '70%', height: '70%'}} />}
                                    </ActionIcon>
                                </Group>
                            </Paper>
                        </SimpleGrid>
                    </Stack>
                </Tabs.Panel>
            </Tabs>
            }

            <Modal opened={apnEventsModalOpened} onClose={apnEventsModalCallbacks.close} title="Property Found Events by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.apnEventsByCounty).map(([county, count], i: number) => (
                        <Box  key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.apnEventsByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={nameEventsModalOpened} onClose={nameEventsModalCallbacks.close} title="Name Found Events by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.nameEventsByCounty).map(([county, count], i: number) => (
                        <Box  key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.nameEventsByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={activeUsers30DaysModalOpened} onClose={activeUsers30DaysModalCallbacks.close} title="Active Users in Last 30 Days by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.activeUsersInLast30DaysByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.activeUsersInLast30DaysByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={newUsers7DaysModalOpened} onClose={newUsers7DaysModalCallbacks.close} title="New Users in Last 7 Days by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.totalRegisteredInLast7DaysByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.totalRegisteredInLast7DaysByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={newUsers30DaysModalOpened} onClose={newUsers30DaysModalCallbacks.close} title="New Users in Last 30 Days by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.totalRegisteredInLast30DaysByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.totalRegisteredInLast30DaysByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={apnModalOpened} onClose={apnModalCallbacks.close} title="Guarded Properties by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.subscriptionsApnByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.subscriptionsApnByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={nameModalOpened} onClose={nameModalCallbacks.close} title="Guarded Names by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.subscriptionsNameByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.subscriptionsNameByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
            <Modal opened={countyUsersModalOpened} onClose={countyUsersModalCallbacks.close} title="Users by County" size="sm">
                <Stack gap={0}>
                    {metrics && Object.entries(metrics.usersByCounty).map(([county, count], i: number) => (
                        <Box key={county}>
                            <Group justify="space-between">
                                <Text fw={700}>{county}</Text>
                                <Text>{count}</Text>
                            </Group>
                            {i < Object.entries(metrics.usersByCounty).length - 1 && <hr style={{width: '100%', borderTop: '1px solid #eee'}} />}
                        </Box>
                    ))}
                </Stack>
            </Modal>
        </>
    )
}
export default Metrics;